<template>
  <v-dialog
    :model-value="true"
    :retain-focus="false"
    width="880"
    max-width="calc(100vw - 40px)"
    persistent
  >
    <section
      class="hrr-dialog"
      role="dialog"
      aria-modal="true"
      aria-label="ハイリスク疑いを運営に報告"
    >
      <h2 class="hrr-dialog__title">
        ハイリスク疑いを運営に報告
      </h2>

      <div class="hrr-dialog__body">
        <v-alert
          v-if="form.errorMessage"
          class="hrr-dialog__error"
          type="error"
          variant="tonal"
          density="compact"
          role="alert"
          closable
          @click:close="form.clearError"
        >
          {{ form.errorMessage }}
        </v-alert>

        <section class="hrr-dialog__panel">
          <h3 class="hrr-dialog__panel-title">
            該当する項目 (複数選択可)
          </h3>
          <!-- 選択肢・分類・ツールチップ文言はすべて GET /new のレスポンス由来。フロントに二重定義しない。 -->
          <HighRiskReportReasonGroup
            v-for="group in newResponse.reasonGroups"
            :key="group.label"
            :group="group"
            :selected-keys="form.reasonKeys"
            :disabled="form.isSubmitting"
            @toggle="form.toggleReason"
          />
        </section>

        <section class="hrr-dialog__panel">
          <h3 class="hrr-dialog__panel-title">
            運営への補足コメント
          </h3>
          <v-textarea
            v-model="form.supplementNote"
            rows="3"
            density="compact"
            variant="outlined"
            hide-details="auto"
            bg-color="#fff"
            :disabled="form.isSubmitting"
            :error="form.overSupplementNoteLimit || form.supplementNoteError !== null"
            :error-messages="form.supplementNoteError ?? []"
          />
          <p
            class="hrr-dialog__counter"
            :class="{ 'hrr-dialog__counter--over': form.overSupplementNoteLimit }"
            aria-live="polite"
          >
            {{ form.supplementNoteLength }} / {{ newResponse.supplementNoteMaxLength }}
          </p>
        </section>
      </div>

      <footer class="hrr-dialog__actions">
        <v-btn
          class="hrr-dialog__cancel"
          variant="flat"
          color="#9e9e9e"
          min-width="112"
          :disabled="form.isSubmitting"
          @click="emit('close')"
        >
          キャンセル
        </v-btn>
        <!-- 報告理由・補足はいずれも任意のため常に活性（AC-002）。送信中のみ無効化する。 -->
        <v-btn
          class="hrr-dialog__submit"
          color="#c8384f"
          variant="flat"
          min-width="112"
          :disabled="!form.submittable"
          :loading="form.isSubmitting"
          @click="handleSubmit"
        >
          運営に報告する
        </v-btn>
      </footer>
    </section>
  </v-dialog>
</template>

<script setup lang="ts">
import type { HighRiskReportAvailableResponse, HighRiskReportResult } from "@/vue/entities/high-risk-report";
import { useHighRiskReport } from "../model/useHighRiskReport";
import HighRiskReportReasonGroup from "./HighRiskReportReasonGroup.vue";

const props = defineProps<{
  newResponse: HighRiskReportAvailableResponse;
  riskAssessmentId: string;
}>();

const emit = defineEmits<{
  close: [];
  reported: [HighRiskReportResult];
}>();

const form = useHighRiskReport({
  consultableType: props.newResponse.consultable.type,
  consultableId: props.newResponse.consultable.id,
  riskAssessmentId: props.riskAssessmentId,
  supplementNoteMaxLength: props.newResponse.supplementNoteMaxLength
});

const handleSubmit = async (): Promise<void> => {
  const result = await form.submit();
  if (result) emit("reported", result);
};
</script>

<style lang="scss" scoped>
// ダイアログ枠は DetailDialog（ティール基調・左寄せヘッダー・右寄せフッター）では
// デザイン（design/images/image.png）に合わないため、リスクアセスメント入力ダイアログと
// 同じ構成で個別に組む。
.hrr-dialog {
  background-color: #fff;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 40px);
  padding: 24px;
}

.hrr-dialog__title {
  color: #c8384f;
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 16px;
  text-align: center;
}

.hrr-dialog__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

.hrr-dialog__error {
  margin-bottom: 12px;
}

// 入力セクションは淡いピンクの面で囲み、選択肢と補足のまとまりを分ける。
.hrr-dialog__panel {
  background-color: #fdeff2;
  border-radius: 8px;
  padding: 16px;

  & + & {
    margin-top: 16px;
  }
}

.hrr-dialog__panel-title {
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 10px;
}

.hrr-dialog__counter {
  color: rgba(0, 0, 0, 0.6);
  font-size: 12px;
  margin-top: 4px;
  text-align: right;
}

.hrr-dialog__counter--over {
  color: #c8384f;
}

.hrr-dialog__actions {
  display: flex;
  gap: 16px;
  justify-content: center;
  margin-top: 20px;
}

.hrr-dialog__cancel,
.hrr-dialog__submit {
  color: #fff;
}
</style>
